import { Helmet } from 'react-helmet-async';
import { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  CircularProgress,
  Pagination,
  Popover,
  MenuItem,
  IconButton,
  Grid,
  TextField,
  Box,
  useMediaQuery,
  useTheme,
  Chip,
  Avatar
} from '@mui/material';

import Iconify from '../components/iconify';
import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';
import CustomModal from '../components/modal/CustomModal';
import FormCreateActivities from '../components/forms/registerParticipantsForms/editParticipants'
import { useAxios } from '../context/AxiosHookContext';
import { useNotification } from '../context/NotificationContext';



export default function ActivityEnrollment() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [participantsList, setActivitiesList] = useState([]);
  const [activity, setActivity] = useState({})
  const navigate = useNavigate();
  const { activityId } = useParams();
  const { showSnackbar } = useNotification();
  const [addShair, setOpenModalAddshair] = useState(false)
  const [valuesToEdit, setValuesToEdit] = useState({})
  const [open, setOpen] = useState(null);
  const [filterName, setFilterName] = useState('');
  const [documento, setDocumento] = useState('');
  const [sort, setSort] = useState(-1);

  if (!activityId) {
    navigate("/404")
  }

  const ACTIVITY_API = `/activities/activities/details/${activityId}`
  const PARTICIPANT_API = `/v2/getRecords?actividadId=${activityId}&page=${page}&size=12&fullName=${filterName ? filterName : ''}&size=10&documento=${documento ? documento : ''}&sort=${sort}`
  const axios = useAxios();

  const handleOpenMenu = (event, values) => {
    setOpen(event.currentTarget);
    setValuesToEdit(values)
  };

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const getAsistants = useCallback(async () => {
    try {
      setActivitiesList([])
      setLoading(true);
      const { data } = await axios.get(PARTICIPANT_API);
      setActivitiesList(data?.data?.list || []);
      setTotalPages(data?.data?.total_pages || 1)
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [axios, PARTICIPANT_API, setActivitiesList, setLoading]);

  const confirm = useCallback(async (participantId) => {
    try {
      await axios.put(`/activities/assistants/${participantId}`, { confirmado: true });
      // Actualizar estado local sin recargar
      setActivitiesList(prev => 
        prev.map(p => 
          p._id === participantId ? { ...p, confirmado: true } : p
        )
      );
    } catch (error) {
      console.log(error);
    }
  }, [axios]);

  const generateReport = async () => {
    try {
      setLoadingReport(true);
      const response = await axios.get(`activities/assistants/asistants/report/${activityId}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activity.actividad}-reporte.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      showSnackbar({
        severity: "success",
        message: "Se ha obtenido con éxito el reporte!",
        title: "Reporte obtenido"
      });
    } catch (error) {
      showSnackbar({
        severity: "error",
        message: "Hemos encontrado un inconveniente al buscar tu reporte.",
        title: "Lo sentimos"
      });
    } finally {
      setLoadingReport(false);
    }
  };

  const getActivityDetails = useCallback(async () => {
    try {
      const { data } = await axios.get(ACTIVITY_API);
      setActivity(data)
    } catch (error) {
      console.log(error);
    }
  }, [axios, ACTIVITY_API]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleFilterByName = (event) => {
    setPage(1);
    setFilterName(event.target.value);
  };

  const handleFilterDocumento = (event) => {
    setPage(1);
    setDocumento(event.target.value);
  };

  const handleChangeSort = (event) => {
    setPage(1);
    setSort(event.target.value);
  };

  useEffect(() => {
    getActivityDetails()
  }, [getActivityDetails])

  useEffect(() => {
    getAsistants();
    getActivityDetails();
  }, [getAsistants, page, filterName, documento, getActivityDetails, sort])

  // Mobile Card Component
  const ParticipantCard = ({ participant }) => (
    <Card sx={{ mb: 2, p: 2, borderRadius: 2, boxShadow: 1 }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
            <Avatar sx={{ 
              width: 40, 
              height: 40, 
              bgcolor: participant.genero === 'M' ? 'primary.main' : 'secondary.main',
              fontSize: '0.9rem'
            }}>
              {participant.nombre?.charAt(0)}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }} noWrap>
                {`${participant.nombre} ${participant?.apellido || ''}`}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                {participant?.documento || 'Sin documento'}
              </Typography>
            </Box>
          </Box>

        </Stack>
        
        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Cargo:</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{participant?.cargo || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Género:</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{participant.genero === 'M' ? 'Masculino' : 'Femenino'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Contacto:</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{participant?.celular || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Email:</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }} noWrap>{participant?.email || 'N/A'}</Typography>
          </Grid>
        </Grid>

        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
          {participant?.confirmado ? (
            <Chip label="✓ Confirmado" color="success" size="small" sx={{ fontSize: '0.7rem', height: 24 }} />
          ) : (
            <Button 
              size="small" 
              variant="contained" 
              onClick={() => confirm(participant._id)}
              sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}
            >
              Confirmar
            </Button>
          )}
          
          {participant?.asiento ? (
            <Chip label={`Asiento: ${participant.asiento}`} color="info" size="small" sx={{ fontSize: '0.7rem', height: 24 }} />
          ) : (
            <Button 
              size="small" 
              variant="outlined" 
              onClick={() => { setValuesToEdit(participant); setOpenModalAddshair(true) }}
              sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}
            >
              Asignar
            </Button>
          )}
        </Stack>
      </Stack>
    </Card>
  );

  return (
    <>
      <Helmet>
        <title>Registros | ASISCORE</title>
      </Helmet>

        <Typography variant={isMobile ? "h5" : "h4"} textAlign="center" mb={2} sx={{ px: 1 }}>
          {activity && activity.actividad}
        </Typography>

        <Stack 
          direction={isMobile ? "column" : "row"} 
          alignItems={isMobile ? "stretch" : "center"} 
          justifyContent="space-between" 
          mb={3}
          spacing={2}
          sx={{ px: { xs: 1, sm: 0 } }}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'row', sm: 'column' }, 
            gap: { xs: 2, sm: 1 },
            justifyContent: { xs: 'space-around', sm: 'flex-start' }
          }}>
            <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
              <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                👨 {activity && activity?.totalM || 0}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>Hombres</Typography>
            </Box>
            <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
              <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                👩 {activity && activity?.totalF || 0}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>Mujeres</Typography>
            </Box>
            <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                {activity && activity?.totalGeneral || 0}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>Total</Typography>
            </Box>
          </Box>

          <Button 
            variant="contained" 
            startIcon={<Iconify icon="file-icons:microsoft-excel" />} 
            onClick={generateReport}
            size={isMobile ? "small" : "medium"}
            fullWidth={isMobile}
            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
          >
            {loadingReport && <CircularProgress size={15} sx={{ color: 'white', mr: 1 }} />}
            {loadingReport ? "Exportando..." : "Exportar"}
          </Button>
        </Stack>

        <Card sx={{ borderRadius: 2 }}>
          <Grid container spacing={1} p={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12} sm={4}>
              <TextField 
                fullWidth 
                size="small" 
                placeholder="Buscar por nombre..." 
                label="Nombre" 
                value={filterName} 
                onChange={handleFilterByName}
                sx={{ '& .MuiInputBase-input': { fontSize: { xs: '0.8rem', sm: '0.875rem' } } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField 
                fullWidth 
                size="small" 
                placeholder="Buscar por cédula..." 
                label="Cédula" 
                value={documento} 
                onChange={handleFilterDocumento}
                sx={{ '& .MuiInputBase-input': { fontSize: { xs: '0.8rem', sm: '0.875rem' } } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField 
                select 
                fullWidth 
                size="small" 
                label="Ordenar por"
                onChange={handleChangeSort} 
                value={sort}
                sx={{ '& .MuiInputBase-input': { fontSize: { xs: '0.8rem', sm: '0.875rem' } } }}
              >
                <MenuItem value={-1}>Más recientes</MenuItem>
                <MenuItem value={1}>Más antiguos</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          {isMobile ? (
            // Mobile View - Cards
            <Box sx={{ p: { xs: 1, sm: 2 }, maxHeight: '60vh', overflowY: 'auto' }}>
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={30} />
                </Box>
              )}
              
              {participantsList.length > 0 && participantsList.map((participant) => (
                <ParticipantCard key={participant._id} participant={participant} />
              ))}
              
              {participantsList.length === 0 && !loading && (
                <Paper sx={{ textAlign: 'center', py: 4, mx: 1 }}>
                  <Typography variant="h6" paragraph sx={{ fontSize: '1rem' }}>No hay registros</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    No se encontraron registros para esta actividad.
                  </Typography>
                </Paper>
              )}
            </Box>
          ) : (
            // Desktop View - Enhanced Cards Grid
            <Box sx={{ p: 1.5 }}>
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={30} />
                </Box>
              )}
              
              <Grid container spacing={1.5}>
                {participantsList.length > 0 && participantsList.map((participant) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={participant._id}>
                    <Card sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      boxShadow: 1,
                      height: '100%',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: 3,
                        transform: 'translateY(-1px)'
                      }
                    }}>
                      <Stack spacing={1.5} sx={{ height: '100%' }}>
                        {/* Header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ 
                            width: 32, 
                            height: 32, 
                            bgcolor: participant.genero === 'M' ? 'primary.main' : 'secondary.main',
                            fontSize: '0.8rem'
                          }}>
                            {participant.nombre?.charAt(0)}
                          </Avatar>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.2 }}>
                              {`${participant.nombre} ${participant?.apellido || ''}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              {participant?.documento || 'Sin doc'}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {/* Info Compact */}
                        <Stack spacing={0.5}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">Cargo:</Typography>
                            <Typography variant="caption" fontWeight={500} sx={{ textAlign: 'right', flex: 1, ml: 1 }}>
                              {participant?.cargo || 'N/A'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">Género:</Typography>
                            <Typography variant="caption" fontWeight={500}>
                              {participant.genero === 'M' ? '👨 M' : '👩 F'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">Tel:</Typography>
                            <Typography variant="caption" fontWeight={500} sx={{ textAlign: 'right', flex: 1, ml: 1 }}>
                              {participant?.celular || 'N/A'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Email:</Typography>
                            <Typography variant="caption" fontWeight={500} sx={{ display: 'block', wordBreak: 'break-all', fontSize: '0.65rem' }}>
                              {participant?.email || 'N/A'}
                            </Typography>
                          </Box>
                          {participant?.organizacion && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary">Org:</Typography>
                              <Typography variant="caption" fontWeight={500} sx={{ textAlign: 'right', flex: 1, ml: 1 }}>
                                {participant.organizacion}
                              </Typography>
                            </Box>
                          )}
                          {participant?.fechaRegistro && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary">Registro:</Typography>
                              <Typography variant="caption" fontWeight={500}>
                                {new Date(participant.fechaRegistro).toLocaleDateString()}
                              </Typography>
                            </Box>
                          )}
                        </Stack>

                        {/* Actions Compact */}
                        <Stack direction="row" spacing={0.5} justifyContent="center" sx={{ mt: 'auto' }}>
                          {participant?.confirmado ? (
                            <Chip 
                              label="✓" 
                              color="success" 
                              size="small"
                              sx={{ fontSize: '0.7rem', height: 20, minWidth: 24 }}
                            />
                          ) : (
                            <Button 
                              size="small"
                              variant="contained" 
                              onClick={() => confirm(participant._id)}
                              sx={{ fontSize: '0.65rem', py: 0.25, px: 1, minWidth: 'auto' }}
                            >
                              Confirmar
                            </Button>
                          )}
                          
                          <Button 
                            size="small"
                            variant={participant?.asiento ? "contained" : "outlined"}
                            color={participant?.asiento ? "info" : "primary"}
                            onClick={() => { setValuesToEdit(participant); setOpenModalAddshair(true) }}
                            sx={{ fontSize: '0.65rem', py: 0.25, px: 1, minWidth: 'auto' }}
                          >
                            {participant?.asiento ? `Asiento ${participant.asiento}` : 'Asignar'}
                          </Button>
                        </Stack>
                      </Stack>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              
              {participantsList.length === 0 && !loading && (
                <Paper sx={{ textAlign: 'center', py: 6, borderRadius: 3 }}>
                  <Typography variant="h5" paragraph color="text.secondary">
                    📋 No hay registros
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    No se encontraron registros para esta actividad.
                  </Typography>
                </Paper>
              )}
            </Box>
          )}

          <Box sx={{ p: { xs: 1, sm: 2 }, display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' } }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handleChangePage}
              size={isMobile ? "small" : "medium"}
            />
          </Box>
        </Card>

      <CustomModal
        open={addShair}
        title={"Editar registro"}
      >
        <FormCreateActivities
          onClose={() => setOpenModalAddshair(false)}
          getValues={() => {
            getAsistants();
            setOpenModalAddshair(false);
            setValuesToEdit({});
            setOpen(null);
          }}
          valuesToEdit={valuesToEdit}
        />
      </CustomModal>


    </>
  );
}